import { backendApi } from "@/services/backend/api";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { DustDensitySample } from "@/services/backend/samples/types/dust-density-sample";
import { Filter } from "@/shared/lib/filter/filter";
import { EntityId } from "@/shared/types/entity-id";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const samplesDustDensityApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["SamplesDustDensitySample"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      samplesDustDensitySamplePut: build.mutation<
        DustDensitySample,
        PutRequest
      >({
        query: (request) => ({
          url: `/samples/dust-density`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesDustDensitySample", id: request.id },
          "SamplesDustDensitySample",
          "SamplesSampleViewModel",
        ],
      }),
      samplesDustDensitySampleUpdateLab: build.mutation<
        DustDensitySample,
        UpdateLabRequest
      >({
        query: (request) => ({
          url: `/samples/dust-density/lab`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "SamplesDustDensitySample", id: request.id },
          "SamplesDustDensitySample",
          "SamplesSampleViewModel",
        ],
      }),
      samplesDustDensitySampleShow: build.query<DustDensitySample, ShowRequest>(
        {
          query: (request) => ({
            url: `/samples/dust-density`,
            method: "GET",
            params: request,
          }),
          providesTags: providesEntity("SamplesDustDensitySample"),
        },
      ),
      samplesDustDensitySampleList: build.query<List, ListRequest>({
        query: (request) => ({
          url: `/samples/dust-density/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) =>
          providesList(result?.samples, "SamplesDustDensitySample"),
      }),
      samplesDustDensitySampleDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/samples/dust-density`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["SamplesDustDensitySample", "SamplesSampleViewModel"],
      }),
    }),
  });

export const {
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleUpdateLabMutation,
  useSamplesDustDensitySampleShowQuery,
  useSamplesDustDensitySampleListQuery,
  useLazySamplesDustDensitySampleListQuery,
  useSamplesDustDensitySampleDeleteMutation,
} = samplesDustDensityApi;

export type PutRequest = DustDensitySample;

export interface UpdateLabRequest {
  id: string;
  labId: EntityId;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest {
  lab?: Filter<string>;
  document?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface List {
  samples: DustDensitySample[];
  count: number;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached dust density sample for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateDustDensitySampleCache = updateCache(
  samplesDustDensityApi,
  "samplesDustDensitySampleShow",
);
